"use client";

import { useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberOr } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

type ConfidenceLevel = "90" | "95" | "99";

const Z_BY_CONFIDENCE: Record<ConfidenceLevel, number> = {
  "90": 1.645,
  "95": 1.96,
  "99": 2.576,
};

const COMMON_MARGINS = [3, 5, 10];

interface SampleSizeResult {
  z: number;
  p: number;
  e: number;
  n0: number;
  n: number;
  population: number | null;
}

/** English strings. A translated content file overrides these through `ui`. */
export const SAMPLE_SIZE_CALCULATOR_STRINGS = {
  confidenceLevel: "Confidence level",
  confidence90: "90% (z = 1.645)",
  confidence95: "95% (z = 1.96)",
  confidence99: "99% (z = 2.576)",
  marginOfError: "Margin of error (%)",
  marginPlaceholder: "5",
  expectedProportion: "Expected proportion (%) — use 50 if unsure",
  proportionPlaceholder: "50",
  populationSize: "Population size — leave blank for infinite",
  populationPlaceholder: "e.g. 10000",
  results: "Results",
  requiredSampleSize: "Required sample size",
  zScoreUsed: "Z-score used",
  population: "Population",
  infinite: "Infinite",
  howThisWasCalculated: "How this was calculated",
  formulaPrefix: "n₀ = z²p(1−p) / e² = ",
  squareTimesMid: "² × ",
  timesOpenParen: " × (1 − ",
  closeParenSlash: ") / ",
  squareEqualsMid: "² = ",
  slashMid: " / ",
  equalsMid: " = ",
  correctionPrefix:
    "Finite population correction: n = n₀ / (1 + (n₀ − 1) / N) = ",
  divideOpenParen1Plus: " / (1 + ",
  closeParenEqualsMid: ") = ",
  rounding:
    "Rounding up to a whole respondent gives a required sample size of {size}. This assumes simple random sampling; add extra respondents to cover expected non-response.",
  sampleSizeAtMargins:
    "Sample size at common margins of error ({confidence}% confidence)",
  marginOfErrorColumn: "Margin of error",
  requiredSampleSizeColumn: "Required sample size",
  emptyResult: "—",
  noResultMessage:
    "Enter a margin of error and expected proportion between 0 and 100 (and a population of at least 1, if given) to see the required sample size.",
} as const;

function computeSampleSize(
  confidence: ConfidenceLevel,
  marginPercent: number,
  proportionPercent: number,
  population: number | null
): SampleSizeResult | null {
  if (
    Number.isNaN(marginPercent) ||
    marginPercent <= 0 ||
    marginPercent >= 100
  ) {
    return null;
  }
  if (
    Number.isNaN(proportionPercent) ||
    proportionPercent <= 0 ||
    proportionPercent >= 100
  ) {
    return null;
  }
  if (population !== null && (Number.isNaN(population) || population < 1)) {
    return null;
  }
  const z = Z_BY_CONFIDENCE[confidence];
  const p = proportionPercent / 100;
  const e = marginPercent / 100;
  const n0 = (z * z * p * (1 - p)) / (e * e);
  let n = n0;
  if (population !== null) {
    n = n0 / (1 + (n0 - 1) / population);
    n = Math.min(n, population);
  }
  return { z, p, e, n0, n: Math.ceil(n), population };
}

function parseOptionalPopulation(text: string): number | null {
  if (text.trim().length === 0) {
    return null;
  }
  return parseNumberOr(text, Number.NaN);
}

function formatNum(value: number, fmt: NumberFormatter): string {
  return fmt(value, { maximumFractionDigits: 4 });
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function SampleSizeCalculatorWidget() {
  const strings = useToolUi(SAMPLE_SIZE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [confidence, setConfidence] = useState<ConfidenceLevel>("95");
  const [margin, setMargin] = useState("5");
  const [proportion, setProportion] = useState("50");
  const [populationText, setPopulationText] = useState("");

  const population = parseOptionalPopulation(populationText);
  const marginValue = parseNumberOr(margin, Number.NaN);
  const proportionValue = parseNumberOr(proportion, Number.NaN);
  const result = computeSampleSize(
    confidence,
    marginValue,
    proportionValue,
    population
  );

  const tableRows = COMMON_MARGINS.map((m) => ({
    margin: m,
    result: computeSampleSize(confidence, m, proportionValue, population),
  }));

  return (
    <>
      {/* Inputs */}
      <section className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ss-confidence"
          >
            {strings.confidenceLevel}
          </label>
          <select
            className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="ss-confidence"
            onChange={(e) => setConfidence(e.target.value as ConfidenceLevel)}
            value={confidence}
          >
            <option value="90">{strings.confidence90}</option>
            <option value="95">{strings.confidence95}</option>
            <option value="99">{strings.confidence99}</option>
          </select>
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ss-margin"
          >
            {strings.marginOfError}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="ss-margin"
            inputMode="decimal"
            onChange={(e) => setMargin(e.target.value)}
            placeholder={strings.marginPlaceholder}
            type="text"
            value={margin}
          />
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ss-proportion"
          >
            {strings.expectedProportion}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="ss-proportion"
            inputMode="decimal"
            onChange={(e) => setProportion(e.target.value)}
            placeholder={strings.proportionPlaceholder}
            type="text"
            value={proportion}
          />
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ss-population"
          >
            {strings.populationSize}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="ss-population"
            inputMode="numeric"
            onChange={(e) => setPopulationText(e.target.value)}
            placeholder={strings.populationPlaceholder}
            type="text"
            value={populationText}
          />
        </div>
      </section>

      {/* Results */}
      {result ? (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.results}
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label={strings.requiredSampleSize}
              value={formatNum(result.n, fmt)}
            />
            <StatCard
              label={strings.zScoreUsed}
              value={formatNum(result.z, fmt)}
            />
            <StatCard
              label={strings.population}
              value={
                result.population === null
                  ? strings.infinite
                  : formatNum(result.population, fmt)
              }
            />
          </div>

          {/* Working */}
          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
            <h3 className="font-semibold text-foreground text-lg">
              {strings.howThisWasCalculated}
            </h3>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              {strings.formulaPrefix}
              {formatNum(result.z, fmt)}
              {strings.squareTimesMid}
              {formatNum(result.p, fmt)}
              {strings.timesOpenParen}
              {formatNum(result.p, fmt)}
              {strings.closeParenSlash}
              {formatNum(result.e, fmt)}
              {strings.squareEqualsMid}
              {formatNum(result.z * result.z * result.p * (1 - result.p), fmt)}
              {strings.slashMid}
              {formatNum(result.e * result.e, fmt)}
              {strings.equalsMid}
              {formatNum(result.n0, fmt)}
            </p>
            {result.population !== null && (
              <p className="font-mono text-muted-foreground text-sm leading-relaxed">
                {strings.correctionPrefix}
                {formatNum(result.n0, fmt)}
                {strings.divideOpenParen1Plus}
                {formatNum(result.n0 - 1, fmt)}
                {strings.slashMid}
                {formatNum(result.population, fmt)}
                {strings.closeParenEqualsMid}
                {formatNum(
                  result.n0 / (1 + (result.n0 - 1) / result.population),
                  fmt
                )}
              </p>
            )}
            <p className="text-muted-foreground text-sm leading-relaxed">
              {renderSlots(strings.rounding, {
                size: (
                  <strong className="text-foreground">
                    {formatNum(result.n, fmt)}
                  </strong>
                ),
              })}
            </p>
          </div>

          {/* Table at common margins */}
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground text-lg">
              {t(strings.sampleSizeAtMargins, { confidence })}
            </h3>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">
                      {strings.marginOfErrorColumn}
                    </th>
                    <th className="px-4 py-3 font-medium">
                      {strings.requiredSampleSizeColumn}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  {tableRows.map((row) => (
                    <tr className="border-border border-t" key={row.margin}>
                      <td className="px-4 py-3">±{row.margin}%</td>
                      <td className="px-4 py-3">
                        {row.result === null
                          ? strings.emptyResult
                          : formatNum(row.result.n, fmt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : (
        <p className="text-muted-foreground text-sm">
          {strings.noResultMessage}
        </p>
      )}
    </>
  );
}
